// İSG uzmanı portalı — İzin detayı: Gerekli Eğitimler (api-contract §17.2). İzne gerekli
// eğitim ekle (mevcut aktif eğitimlerden çoklu seç) / kaldır. Ekleme `POST
// /permits/{uuid}/trainings` **replace** semantiğiyle (mevcut + yeni birleşimi gönderilir);
// kaldırma `DELETE /permits/{uuid}/trainings/{trainingUuid}` tek eğitimi çıkarır. Aksiyon
// sonrası izin detayı invalidate (readiness yeniden türetilir). Gerekli eğitim verisi üst
// detay sorgusundan gelir (senkron).

import {
  Button,
  ConfirmDialog,
  EmptyState,
  type RequiredTrainingDto,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableToolbar,
  permitsApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permitsKeys } from '../../lib/permitsKeys';
import { RequiredTrainingAddModal } from './RequiredTrainingAddModal';

interface RequiredTrainingsPanelProps {
  permitUuid: string;
  requiredTrainings: RequiredTrainingDto[];
}

export function RequiredTrainingsPanel({
  permitUuid,
  requiredTrainings,
}: RequiredTrainingsPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<RequiredTrainingDto | null>(null);

  const removeMutation = useMutation({
    mutationFn: (trainingUuid: string) => permitsApi.removePermitTraining(permitUuid, trainingUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setToRemove(null);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      {/* Dolu (primary) "Eğitim Ekle" düğmesi → `TableToolbar`'ın ghost "New" aksiyonu (S33 Slice
          6, coordinator bulgusu: ekranın TEK dolu butonu Onayla kalmalı, bkz. `PermitVehiclesPanel
          .tsx`'in AYNI notu). */}
      <TableToolbar
        summary={t('permits.trainings.subtitle')}
        onNew={() => setAddOpen(true)}
        newLabel={t('permits.trainings.add')}
      />

      <Table caption={t('permits.detail.section.trainings')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('permits.trainings.add.field.trainings')}</TableHead>
            <TableHead className="text-right">{t('permits.common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requiredTrainings.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={GraduationCap} title={t('permits.trainings.empty')} />
            </TableMessageRow>
          ) : (
            requiredTrainings.map((tr) => (
              <TableRow key={tr.trainingUuid}>
                <TableCell className="font-medium text-fg">{tr.title}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToRemove(tr)}
                      aria-label={t('permits.trainings.action.remove')}
                    >
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <RequiredTrainingAddModal
        open={addOpen}
        onOpenChange={setAddOpen}
        permitUuid={permitUuid}
        current={requiredTrainings}
      />

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={t('permits.trainings.remove.title')}
        body={t('permits.trainings.remove.body', { title: toRemove?.title ?? '' })}
        confirmLabel={t('permits.trainings.remove.confirm')}
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.trainingUuid)}
      />
    </div>
  );
}
